import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiBox } from "react-icons/fi";
import useMerchantProduct from "../../../Routes/Marketplace/Merchant/lib/fetchMerchantProduct";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatCard from "../../components/StatCard";
import {
  getMerchantProductStock,
  isLowStock,
  isOutOfStock,
  LOW_STOCK_THRESHOLD,
} from "../utils/productStock";

const STOCK_FILTERS = [
  { id: "all", label: "All products" },
  { id: "low", label: "Low stock" },
  { id: "out", label: "Out of stock" },
];

const MerchantInventoryPage = () => {
  const { data, isLoading, isError } = useMerchantProduct();
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("all");

  const allProducts = useMemo(
    () => data?.pages.flatMap((p) => p.products) || [],
    [data]
  );

  const stats = useMemo(
    () => ({
      total: allProducts.length,
      low: allProducts.filter((p) => isLowStock(p) && !isOutOfStock(p)).length,
      out: allProducts.filter((p) => isOutOfStock(p)).length,
    }),
    [allProducts]
  );

  const products = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allProducts
      .filter((product) => {
        const stock = getMerchantProductStock(product);
        if (stockFilter === "low" && !(isLowStock(product) && stock > 0)) return false;
        if (stockFilter === "out" && !isOutOfStock(product)) return false;
        if (!term) return true;
        return (
          (product.name || "").toLowerCase().includes(term) ||
          (product.category || "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => getMerchantProductStock(a) - getMerchantProductStock(b));
  }, [allProducts, search, stockFilter]);

  const columns = [
    { key: "name", label: "Product" },
    { key: "category", label: "Category" },
    {
      key: "stock",
      label: "Stock",
      render: (r) => {
        const stock = getMerchantProductStock(r);
        const variant = stock === 0 ? "danger" : stock <= LOW_STOCK_THRESHOLD ? "warning" : "success";
        return <span className={`dashboard-badge dashboard-badge--${variant}`}>{stock}</span>;
      },
    },
    {
      key: "approval",
      label: "Status",
      render: (r) => r.approvalStatus || "approved",
    },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <Link to={`/merchant/products/${r.slug || r._id}`} className="dashboard-btn dashboard-btn--secondary">
          {isOutOfStock(r) || isLowStock(r) ? "Restock" : "Edit"}
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Inventory"
        subtitle="Full stock levels across your catalog"
        icon={FiBox}
      />
      <div className="dashboard-stat-grid">
        <StatCard label="Total SKUs" value={stats.total} />
        <StatCard label="Low stock" value={stats.low} />
        <StatCard label="Out of stock" value={stats.out} />
      </div>
      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search products…">
        <div className="dashboard-filter-pills">
          {STOCK_FILTERS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={stockFilter === id ? "active" : ""}
              onClick={() => setStockFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </FilterBar>
      {isError && <p style={{ color: "var(--dash-danger)" }}>Failed to load inventory.</p>}
      {!isLoading && products.length === 0 && !isError && (
        <EmptyState
          icon={FiBox}
          title={stockFilter === "all" ? "No products yet" : "No matches"}
          message={
            stockFilter === "all"
              ? "Add products to track inventory here."
              : "Try a different stock filter or search term."
          }
        />
      )}
      <DataTable columns={columns} rows={products.map((p) => ({ ...p, _key: p._id }))} loading={isLoading} />
    </>
  );
};

export default MerchantInventoryPage;
